import { useMemo, useRef, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminEventMailingsImpl from "./AdminEventMailings.jsx";

// Sprint 09I production-acceptance adapter.
// Keeps the existing implementation intact and fixes only acceptance issues
// discovered in the live admin flow.
//
// The `error.response.data.detail` array-vs-string normalization that used to
// live here was moved to lib/apiError.js's installApiErrorNormalizer(), which
// is installed on the passed-in authAxios itself (see lib/auth.jsx) -- an
// error reaching this adapter's own catch blocks is already normalized, so
// this file no longer needs its own copy of that logic.

function createMailingAxiosAdapter(authAxios, onMailingDetail) {
  const call = async (method, ...args) => method(...args);

  const get = async (url, config) => {
    const response = await call(authAxios.get.bind(authAxios), url, config);
    const detailMatch = typeof url === "string" && url.match(/^\/admin\/mailings\/([^/]+)$/);
    if (detailMatch && response?.data) {
      const normalized =
        response.data.mailing_type === "route_info" && !response.data.include_route_info
          ? { ...response.data, include_route_info: true }
          : response.data;
      onMailingDetail?.(normalized);
      return normalized === response.data ? response : { ...response, data: normalized };
    }
    return response;
  };

  const patch = (url, data, config) => {
    let payload = data;
    if (typeof url === "string" && /^\/admin\/mailings\/[^/]+$/.test(url) && payload?.mailing_type === "route_info") {
      payload = { ...payload, include_route_info: true };
    }
    return call(authAxios.patch.bind(authAxios), url, payload, config);
  };

  const post = (url, data, config) => {
    let payload = data;
    const isCreateMailing = typeof url === "string" && /^\/admin\/events\/[^/]+\/mailings$/.test(url);
    if (isCreateMailing && payload && payload.subject === "") {
      payload = { ...payload, subject: "Praktische informatie" };
    }
    if (isCreateMailing && payload?.mailing_type === "route_info") {
      payload = { ...payload, include_route_info: true };
    }
    return call(authAxios.post.bind(authAxios), url, payload, config);
  };

  return {
    get,
    patch,
    post,
    put: (...args) => call(authAxios.put.bind(authAxios), ...args),
    delete: (...args) => call(authAxios.delete.bind(authAxios), ...args),
    request: (...args) => call(authAxios.request.bind(authAxios), ...args),
  };
}

export default function AdminEventMailings(props) {
  const rootRef = useRef(null);
  const [currentMailing, setCurrentMailing] = useState(null);
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const adaptedAuthAxios = useMemo(
    () => createMailingAxiosAdapter(props.authAxios, setCurrentMailing),
    [props.authAxios],
  );

  const goPreviousStep = () => {
    const buttons = Array.from(rootRef.current?.querySelectorAll("button") || []);
    const previous = buttons.find((button) => button.textContent?.trim().startsWith("Vorige"));
    if (previous && !previous.disabled) {
      previous.click();
      return;
    }
    toast.info("Je bent al bij de eerste configuratiestap");
  };

  const deleteDraft = async () => {
    if (!currentMailing?.id || currentMailing.status !== "draft") return;
    if (!window.confirm(`Concept “${currentMailing.name || "Nieuwe mailing"}” verwijderen?`)) return;
    setDeleting(true);
    try {
      await adaptedAuthAxios.delete(`/admin/mailings/${currentMailing.id}`);
      toast.success("Concept verwijderd");
      setCurrentMailing(null);
      setRevision((value) => value + 1);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message);
    } finally {
      setDeleting(false);
    }
  };

  const isDraftDetail = currentMailing?.status === "draft";

  return (
    <div ref={rootRef}>
      {isDraftDetail && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-svj-border bg-svj-bg/60 p-3">
          <button
            type="button"
            onClick={goPreviousStep}
            className="inline-flex items-center gap-1.5 rounded-lg border border-svj-border px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Vorige configuratiestap
          </button>
          <button
            type="button"
            onClick={deleteDraft}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-400 hover:bg-red-500/10 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Verwijderen…" : "Concept verwijderen"}
          </button>
        </div>
      )}
      <AdminEventMailingsImpl key={revision} {...props} authAxios={adaptedAuthAxios} />
    </div>
  );
}
